import React, { useState } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import PaymentFailed from "Containers/Banner/PaymentFailed";
import { ButtonWrapper, Button } from "ds/Button";
import Modal from "ds/Modal";
import useDecodedParams from "Hooks/useDecodedParams";
import { useLocalForage } from "Hooks/useLocalForage";
import { meSelector } from "Reducers/app/selectors";
import { selectFeatureOrganizationsEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { useLoadOrders } from "Reducers/organization/order/hooks/useLoadOrders";
import { useAppSelector } from "Store/hooks";

const OrganizationPaymentFailed = () => {
  const { organizationId } = useDecodedParams<{ organizationId: string }>();

  const { value: dismissFailedBanner } = useLocalForage(
    "payment-failed-dismissed-banner"
  );

  const { value: dismissFailedModal, setValue: setDismissFailedModal } =
    useLocalForage("payment-failed-dismissed-modal");

  const [isOpen, setIsOpen] = useState(true);

  const intl = useIntl();

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );

  const me = useAppSelector(meSelector);
  const organizationEnabled = useAppSelector(selectFeatureOrganizationsEnabled);

  const ownerId = organization?.owner_id;
  const userId = me?.id;

  const { hasUnpaidOrders } = useLoadOrders(organizationId, userId, ownerId);

  if (!me) return null;

  let billingUrl = `/-/users/${me.username}/billing`;
  let invoicesUrl = `/-/users/${me.username}/billing/history`;

  if (organizationEnabled) {
    billingUrl = `/${organizationId}/-/billing`;
    invoicesUrl = `/${organizationId}/-/billing/history`;
  }

  if (!hasUnpaidOrders) return null;

  return (
    <>
      {dismissFailedBanner !== true && (
        <PaymentFailed organizationId={organizationId} />
      )}

      {dismissFailedModal !== true && (
        <Modal
          isOpen={isOpen}
          size="medium"
          onOpenChange={() => {
            setIsOpen(false);
            setDismissFailedModal(true);
          }}
          handleClose={() => {
            setIsOpen(false);
          }}
        >
          <Typography.Title tag="h3">
            {intl.formatMessage({
              id: "modal.organization.project.title.paymentfailed",
              defaultMessage: "Payment failed"
            })}
          </Typography.Title>
          <Typography.BodyText>
            {intl.formatMessage({
              id: "modal.organization.project.body.paymentfailed",
              defaultMessage:
                "Please check your invoice and billing details or contact your billing provider to resolve your failed payment."
            })}
          </Typography.BodyText>

          <ButtonWrapper spacing="modal" justifyContent="end">
            <Button
              variant="secondary"
              to={invoicesUrl}
              analyticId="modal.organization.project.button.invoices.paymentfailed"
            >
              {intl.formatMessage({
                id: "modal.organization.project.button.invoices.paymentfailed",
                defaultMessage: "Invoices"
              })}
            </Button>

            <Button
              to={billingUrl}
              analyticId="modal.organization.project.button.billing.paymentfailed"
            >
              {intl.formatMessage({
                id: "modal.organization.project.button.billing.paymentfailed",
                defaultMessage: "Billing details"
              })}
            </Button>
          </ButtonWrapper>
        </Modal>
      )}
    </>
  );
};

export default OrganizationPaymentFailed;
